/**
 * FreshnessBadge - Displays freshness indicator for by_product_p lots
 * Shows age-based badge: Fresh (green), Use Soon (orange), Old Stock (red)
 */
import React from 'react';
import { useLanguage } from '../i18n/LanguageContext';

interface FreshnessBadgeProps {
  ageDays: number | null | undefined;
  badgeKey: 'fresh' | 'use_soon' | 'old_stock' | string | null | undefined;
  badgeLabel?: string | null;
  badgeColor?: 'green' | 'orange' | 'red' | string | null;
}

const colorClasses: Record<string, string> = {
  green: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  orange: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
  red: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
};

export const FreshnessBadge: React.FC<FreshnessBadgeProps> = ({
  ageDays,
  badgeKey,
  badgeLabel,
  badgeColor,
}) => {
  const { t } = useLanguage();

  // Don't render if no badge key (not a by_product_p lot or no data)
  if (!badgeKey) return null;

  // Get translated label or use provided label
  const labelMap: Record<string, string> = {
    fresh: t('freshness.fresh'),
    use_soon: t('freshness.useSoon'),
    old_stock: t('freshness.oldStock'),
  };
  const label = labelMap[badgeKey] || badgeLabel || badgeKey;

  // Build display text: "Fresh" for day 0, "Fresh - 3d" for other days
  const displayText = ageDays === 0 || ageDays === null
    ? label
    : `${label} - ${ageDays}d`;

  const colorClass = colorClasses[badgeColor || 'green'] || colorClasses.green;

  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${colorClass}`}>
      {displayText}
    </span>
  );
};

export default FreshnessBadge;
